// ===================================
// KAIXO - Modal de Ajustes
// ===================================

import React, { useState } from 'react';
import './SettingsModal.css';
import { Icons } from '../Icons/Icons';
import ui from '../../i18n/ui';
import api from '../../services/api';
import InviteFriendsModal from '../InviteFriendsModal/InviteFriendsModal';
import VerificationModal from '../VerificationModal/VerificationModal';
import PlansModal from '../Plans/PlansModal';
import MyBillingModal from '../MyBilling/MyBillingModal';
import WebLinkScanner from '../WebLinkScanner/WebLinkScanner';
import useModalClose from '../../hooks/useModalClose';

function SettingsModal({ onClose }) {
    useModalClose(onClose);
    const [isDark, setIsDark] = useState(document.body.classList.contains('dark-mode'));
    const [channelNotifs, setChannelNotifs] = useState(() => {
        try { return localStorage.getItem('kaixo_channel_notifs') !== '0'; } catch { return true; }
    });
    const [accountType, setAccountType] = useState(() => {
        try { return JSON.parse(localStorage.getItem('kaixo_user') || '{}').accountType || 'personal'; } catch { return 'personal'; }
    });

    const [hasChanges, setHasChanges] = useState(false);
    const [showInvite, setShowInvite] = useState(false);
    const [showVerify, setShowVerify] = useState(false);
    const [showPlans, setShowPlans] = useState(false);
    const [showBilling, setShowBilling] = useState(false);
    const [showWebLink, setShowWebLink] = useState(false);
    const [webLinkSuccess, setWebLinkSuccess] = useState(false);

    const toggleAccountType = () => {
        setAccountType(prev => prev === 'personal' ? 'business' : 'personal');
        setHasChanges(true);
    };

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    const handleSave = async () => {
        setSaveError('');
        setSaving(true);
        let user;
        try {
            user = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        } catch {
            setSaving(false);
            setSaveError('No se pudo leer el usuario');
            return;
        }
        try {
            // Persistimos en BD primero. Solo si la BD acepta el cambio
            // tocamos localStorage; así no quedan en estado inconsistente.
            await api.put('/users/profile', { userId: user._id, accountType });
            user.accountType = accountType;
            localStorage.setItem('kaixo_user', JSON.stringify(user));
            window.dispatchEvent(new CustomEvent('user_updated', { detail: user }));
            onClose();
        } catch (err) {
            setSaveError(err.response?.data?.error || err.message || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const toggleDark = () => {
        const next = !isDark;
        document.body.classList.toggle('dark-mode', next);
        localStorage.setItem('kaixo_dark_mode', next ? '1' : '0');
        setIsDark(next);
    };

    const toggleChannelNotifs = () => {
        const next = !channelNotifs;
        localStorage.setItem('kaixo_channel_notifs', next ? '1' : '0');
        setChannelNotifs(next);
        // Emitir evento para que ConversationList actualice el badge
        window.dispatchEvent(new CustomEvent('settings_changed', { detail: { channelNotifs: next } }));
    };

    const getCurrentUser = () => {
        try { return JSON.parse(localStorage.getItem('kaixo_user') || '{}'); }
        catch { return {}; }
    };

    return (
        <div className="settings-overlay" onClick={onClose}>
            <div className="settings-modal" onClick={e => e.stopPropagation()}>
                <div className="settings-header">
                    <h3>{ui.common.settings}</h3>
                    <button className="settings-close" onClick={onClose} aria-label={ui.common.close}>
                        <Icons.Close size={18} color="white" />
                    </button>
                </div>

                <div className="settings-body">
                    <div className="settings-row" onClick={toggleDark}>
                        {isDark
                            ? <><span>☀️ {ui.common.lightMode?.replace(/☀️\s?/, '') || 'Modo claro'}</span></>
                            : <><span>🌙 {ui.common.darkMode?.replace(/🌙\s?/, '') || 'Modo oscuro'}</span></>
                        }
                        <div className={`settings-toggle ${isDark ? 'active' : ''}`}>
                            <div className="settings-toggle-knob" />
                        </div>
                    </div>
                    <div className="settings-row" onClick={toggleChannelNotifs}>
                        {channelNotifs
                            ? <><Icons.Bell size={16} /> <span>{ui.common.notifications || 'Notificaciones'}</span></>
                            : <><Icons.BellOff size={16} /> <span>{ui.common.notifications || 'Notificaciones'}</span></>
                        }
                        <div className={`settings-toggle ${channelNotifs ? 'active' : ''}`}>
                            <div className="settings-toggle-knob" />
                        </div>
                    </div>
                    <div className="settings-separator" />
                    <div className="settings-row settings-row-action" onClick={() => setShowVerify(true)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 16 }}>✓</span>
                            <span>Verifícate</span>
                        </div>
                        <Icons.ChevronRight size={16} color="var(--text-secondary)" />
                    </div>
                    {/* Suscripción / cancelar — Apple y Google exigen que el
                        usuario pueda gestionar su suscripción a 1-2 taps. */}
                    <div className="settings-row settings-row-action" onClick={() => setShowPlans(true)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 16 }}>💳</span>
                            <span>{ui.common.mySubscription || 'Mi suscripción'}</span>
                        </div>
                        <Icons.ChevronRight size={16} color="var(--text-secondary)" />
                    </div>
                    {/* Historial de pagos / Facturas — también requerido por
                        las stores para que el usuario pueda revisar pagos
                        pasados sin contactar a soporte. */}
                    <div className="settings-row settings-row-action" onClick={() => setShowBilling(true)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 16 }}>🧾</span>
                            <span>{ui.common.myPayments || 'Mis pagos'}</span>
                        </div>
                        <Icons.ChevronRight size={16} color="var(--text-secondary)" />
                    </div>
                    <div className="settings-separator" />
                    <div className="settings-row settings-row-action" onClick={() => setShowWebLink(true)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 16 }}>🌐</span>
                            <span>{ui.common.linkWeb || 'Vincular KAIXO web'}</span>
                        </div>
                        <Icons.ChevronRight size={16} color="var(--text-secondary)" />
                    </div>
                    {webLinkSuccess && (
                        <div style={{ padding: '6px 14px', fontSize: 12, color: '#4ade80', textAlign: 'center' }}>
                            ✓ Navegador vinculado correctamente
                        </div>
                    )}
                    <div className="settings-separator" />
                    <div className="settings-row settings-row-action" onClick={() => setShowInvite(true)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Icons.Share size={16} />
                            <span>{ui.common.invite || 'Invitar amigos'}</span>
                        </div>
                        <Icons.ChevronRight size={16} color="var(--text-secondary)" />
                    </div>
                    <div className="settings-separator" />
                    <div className="settings-row" onClick={toggleAccountType}>
                        {accountType === 'business'
                            ? <><Icons.Store size={16} color="#00b4d8" /> <span style={{ color: '#00b4d8' }}>{ui.common.businessMode || 'Modo negocio'}</span></>
                            : <><Icons.User size={16} /> <span>{ui.common.personalMode || 'Modo personal'}</span></>
                        }
                        <div className={`settings-toggle ${accountType === 'business' ? 'active' : ''}`} style={accountType === 'business' ? { background: '#00b4d8' } : {}}>
                            <div className="settings-toggle-knob" />
                        </div>
                    {hasChanges && (
                        <button className="settings-save" onClick={handleSave} disabled={saving}>
                            <Icons.Save size={16} color="white" /> {saving ? '...' : (ui.common.save || 'Guardar')}
                        </button>
                    )}
                    </div>
                    {saveError && (
                        <div style={{ padding: 10, color: '#ef4444', fontSize: 12, textAlign: 'center' }}>
                            {saveError}
                        </div>
                    )}
                </div>
            </div>
            {showInvite && (
                <InviteFriendsModal
                    currentUser={getCurrentUser()}
                    onClose={() => setShowInvite(false)}
                />
            )}
            {showVerify && (
                <VerificationModal
                    currentUser={getCurrentUser()}
                    onClose={() => setShowVerify(false)}
                />
            )}
            {showPlans && (
                <PlansModal onClose={() => setShowPlans(false)} />
            )}
            {showBilling && (
                <MyBillingModal onClose={() => setShowBilling(false)} />
            )}
            {showWebLink && (
                <WebLinkScanner
                    onClose={() => setShowWebLink(false)}
                    onSuccess={() => {
                        setWebLinkSuccess(true);
                        setTimeout(() => setWebLinkSuccess(false), 6000);
                    }}
                />
            )}
        </div>
    );
}

export default SettingsModal;
